import React, { useRef, useState, useEffect } from 'react';
import { modifyQortexXml, uploadAlignmentPcdBundle } from '../../api/client';
import { PcdBundleUploadResponse, Sensor } from '../../api/models';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { formatInputNumber, toDegrees, toRadians } from '../../utils/formatting';
import { invalidateBinPointCloudCache } from '../../utils/loadBinPointCloud';
import { alignedPoseDiffersFromLive, hasAlignedPoseSnapshot } from '../../utils/alignedPose';
import { useSensorPoseEditSession } from '../../hooks/useSensorPoseEditSession';
import QortexSyncModal from '../modals/QortexSyncModal';
import PcdStatusModal from '../modals/PcdStatusModal';

const panelStyle: React.CSSProperties = {
  background: 'rgba(25, 35, 45, 0.8)',
  color: 'white',
  padding: '15px',
  borderRadius: '8px',
  border: '1px solid #334155',
};

const buttonStyle: React.CSSProperties = {
  background: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: '#334155',
};

const compactButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  padding: '6px 8px',
  fontSize: '12px',
};

const inputStyle: React.CSSProperties = {
  width: '72px',
  background: '#334155',
  color: 'white',
  border: '1px solid #475569',
  borderRadius: '4px',
  padding: '4px 6px',
  fontSize: '12px',
};

const ALIGNMENT_MOVEMENT_HELP_LINES = [
  '← → move X',
  '↑ ↓ move Y',
  'PgUp / PgDn move Z (floor unlocked)',
  ', / . adjust yaw',
  'T translate · R rotate',
  'Shift coarse · Ctrl fine',
  'Tab next sensor · Space apply',
  'L floor lock · Ctrl+Z undo',
] as const;

const axisFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '6px',
  marginBottom: '8px',
};

const axisLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
  minWidth: 0,
};

const axisInputStyle: React.CSSProperties = {
  ...inputStyle,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
};

const helpPopoverStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  right: 0,
  zIndex: 20,
  width: '220px',
  padding: '10px 12px',
  borderRadius: '6px',
  background: '#0f172a',
  border: '1px solid #475569',
  color: '#e2e8f0',
  fontSize: '11px',
  lineHeight: 1.45,
  whiteSpace: 'pre-line',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
  pointerEvents: 'none',
};

const MovementHelpButton: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{ position: 'absolute', top: 0, right: 0 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        style={{
          ...compactButtonStyle,
          width: '26px',
          height: '26px',
          padding: 0,
          borderRadius: '999px',
          fontSize: '13px',
          fontWeight: 700,
          lineHeight: 1,
        }}
        data-testid="alignment-movement-help"
        aria-label="Movement keyboard shortcuts"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <div style={helpPopoverStyle} data-testid="alignment-movement-help-popover" role="tooltip">
          {ALIGNMENT_MOVEMENT_HELP_LINES.join('\n')}
        </div>
      )}
    </div>
  );
};

const POSITION_FIELD_TITLES = {
  X: 'Arrow Left / Arrow Right',
  Y: 'Arrow Down / Arrow Up',
  Z: 'Page Down / Page Up (when floor is unlocked)',
} as const;

const ROTATION_FIELD_TITLES = {
  Roll: 'Edit directly or use scene handles (no keyboard nudge)',
  Pitch: 'Edit directly or use scene handles (no keyboard nudge)',
  Yaw: 'Comma / Period (less / more yaw)',
} as const;

const statusColor = (status?: string) => {
  if (status === 'aligned') return '#10b981';
  if (status === 'unaligned') return '#f59e0b';
  if (status === 'none' || !status) return '#94a3b8';
  return '#cbd5e1';
};

const floorLockColor = (locked: boolean) => (locked ? '#38bdf8' : '#64748b');

const renderSensorStatusBadges = (sensor: Sensor) => {
  if (!sensor.pcdUrl) {
    return <span style={{ color: '#94a3b8' }}>no pcd</span>;
  }

  const alignmentStatus = sensor.alignmentStatus || 'unaligned';
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
      <span style={{ color: statusColor(alignmentStatus) }}>{alignmentStatus}</span>
      <span style={{ color: '#475569' }}>·</span>
      <span
        style={{ color: floorLockColor(Boolean(sensor.floorLocked)) }}
        title={sensor.floorLocked ? 'Floor locked (auto-floor applied)' : 'Floor not locked'}
        data-testid={`alignment-floor-status-${sensor.id}`}
      >
        {sensor.floorLocked ? 'floor locked' : 'floor open'}
      </span>
    </span>
  );
};

const TRANSLATE_STEP = 0.2;
const ROTATE_STEP_DEG = 5;

const nudgeSensor = (
  sensor: Sensor,
  updateSensor: (id: string, props: Partial<Sensor>) => void,
  pushSensorPoseHistory: (sensorId: string) => void,
  changes: { position?: [number, number, number]; rotation?: [number, number, number] }
) => {
  pushSensorPoseHistory(sensor.id);
  updateSensor(sensor.id, changes);
};

const toggleFloorLock = (
  sensor: Sensor,
  updateSensor: (id: string, props: Partial<Sensor>) => void,
  pushSensorPoseHistory: (sensorId: string) => void
) => {
  pushSensorPoseHistory(sensor.id);
  const updates: Partial<Sensor> = { floorLocked: !sensor.floorLocked };
  if (sensor.alignmentStatus === 'aligned') {
    updates.alignmentStatus = 'unaligned';
  }
  updateSensor(sensor.id, updates);
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
};

type UploadPhase = 'idle' | 'uploading' | 'analyzing';

const progressTrackStyle: React.CSSProperties = {
  marginTop: '8px',
  height: '6px',
  borderRadius: '999px',
  background: '#334155',
  overflow: 'hidden',
};

const progressFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '999px',
  background: '#4f46e5',
  transition: 'width 0.15s ease-out',
};

const indeterminateBarKeyframes = `
@keyframes alignment-upload-indeterminate {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}
`;

const UploadProgressBar: React.FC<{ phase: UploadPhase; percent: number }> = ({ phase, percent }) => {
  if (phase === 'idle') {
    return null;
  }

  if (phase === 'analyzing') {
    return (
      <div data-testid="alignment-upload-progress">
        <style>{indeterminateBarKeyframes}</style>
        <p style={{ margin: '8px 0 4px', fontSize: '12px', color: '#94a3b8' }}>Analyzing PCDs…</p>
        <div style={progressTrackStyle} aria-hidden="true">
          <div
            style={{
              ...progressFillStyle,
              width: '35%',
              animation: 'alignment-upload-indeterminate 1.2s ease-in-out infinite',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="alignment-upload-progress">
      <p style={{ margin: '8px 0 4px', fontSize: '12px', color: '#94a3b8' }}>Uploading… {percent}%</p>
      <div
        style={progressTrackStyle}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div style={{ ...progressFillStyle, width: `${percent}%` }} />
      </div>
    </div>
  );
};

const AlignmentPanel: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const qortexSettingsInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<PcdBundleUploadResponse | null>(null);
  const [alignmentActionMessage, setAlignmentActionMessage] = useState<string | null>(null);
  const [isAutoFlooring, setIsAutoFlooring] = useState(false);
  const [isRunningIcp, setIsRunningIcp] = useState(false);
  const [isExportingQortex, setIsExportingQortex] = useState(false);
  const [exportQortexMessage, setExportQortexMessage] = useState<string | null>(null);
  const [isQortexSyncOpen, setIsQortexSyncOpen] = useState(false);
  const [isPcdStatusOpen, setIsPcdStatusOpen] = useState(false);
  const [isExportSettingsDialogOpen, setIsExportSettingsDialogOpen] = useState(false);

  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const sensors = useSceneStore((state: SceneState) => state.sensors);
  const selectedObjectId = useSceneStore((state: SceneState) => state.selectedObjectId);
  const updateCurrentProject = useSceneStore((state: SceneState) => state.updateCurrentProject);
  const updateSensor = useSceneStore((state: SceneState) => state.updateSensor);
  const setSelectedObjectId = useSceneStore((state: SceneState) => state.setSelectedObjectId);
  const transformMode = useSceneStore((state: SceneState) => state.transformMode);
  const setTransformMode = useSceneStore((state: SceneState) => state.setTransformMode);
  const commitSensorAlignment = useSceneStore((state: SceneState) => state.commitSensorAlignment);
  const resetSensorToDesign = useSceneStore((state: SceneState) => state.resetSensorToDesign);
  const restoreSensorToAligned = useSceneStore((state: SceneState) => state.restoreSensorToAligned);
  const alignmentConnectivity = useSceneStore((state: SceneState) => state.alignmentConnectivity);
  const advanceAlignmentSensor = useSceneStore((state: SceneState) => state.advanceAlignmentSensor);
  const autoFloorSensor = useSceneStore((state: SceneState) => state.autoFloorSensor);
  const runIcpSensor = useSceneStore((state: SceneState) => state.runIcpSensor);
  const pushSensorPoseHistory = useSceneStore((state: SceneState) => state.pushSensorPoseHistory);
  const undoSensorPose = useSceneStore((state: SceneState) => state.undoSensorPose);
  const redoSensorPose = useSceneStore((state: SceneState) => state.redoSensorPose);
  const sensorPoseHistory = useSceneStore((state: SceneState) => state.sensorPoseHistory);
  const sensorPoseRedoHistory = useSceneStore((state: SceneState) => state.sensorPoseRedoHistory);
  const { beginSensorPoseEdit, finishSensorPoseEdit } = useSensorPoseEditSession(pushSensorPoseHistory);
  const refreshAlignmentConnectivity = useSceneStore((state: SceneState) => state.refreshAlignmentConnectivity);
  const selectedSensor = sensors.find((sensor) => sensor.id === selectedObjectId);
  const sensorsWithPcd = sensors.filter((sensor) => sensor.pcdUrl);
  const sensorsMissingPcd = sensors.filter((sensor) => !sensor.pcdUrl);
  const placedCount = alignmentConnectivity?.placed_count
    ?? sensorsWithPcd.filter((sensor) => sensor.alignmentStatus === 'aligned').length;
  const totalAlignable = alignmentConnectivity?.total_alignable ?? sensorsWithPcd.length;
  const recommendedNext = alignmentConnectivity?.recommended_next;
  const originSensorId = alignmentConnectivity?.origin_sensor_id ?? null;
  const originSensor = originSensorId
    ? sensors.find((sensor) => sensor.id === originSensorId)
    : undefined;
  const sensorStatusSignature = sensors.map((sensor) => `${sensor.id}:${sensor.alignmentStatus}`).join('|');

  useEffect(() => {
    if (currentProject) {
      void refreshAlignmentConnectivity();
    }
  }, [
    currentProject?.id,
    selectedObjectId,
    sensorStatusSignature,
    refreshAlignmentConnectivity,
  ]);

  const handleUpload = async (file: File) => {
    if (!currentProject) {
      setUploadError('Please select a project first.');
      return;
    }

    setIsUploading(true);
    setUploadPhase('uploading');
    setUploadPercent(0);
    setUploadError(null);

    try {
      const result = await uploadAlignmentPcdBundle(currentProject.id, file, (percent) => {
        setUploadPercent(percent);
        if (percent >= 100) {
          setUploadPhase('analyzing');
        }
      });
      for (const match of result.matched) {
        if (match.pcd_url) {
          invalidateBinPointCloudCache(match.pcd_url);
        }
      }
      updateCurrentProject(result.project);
      setUploadResult(result);
      setIsPcdStatusOpen(true);
    } catch (error: unknown) {
      let message = 'Failed to upload PCD bundle.';
      if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as { code?: string }).code;
        if (code === 'ECONNABORTED') {
          message = 'Upload timed out. Large archives can take several minutes - please try again.';
        } else if (code === 'ERR_NETWORK') {
          message =
            'Network error while uploading. The server may still be processing a large archive, or it may have run out of memory. Check server logs and retry.';
        }
      }
      if (message === 'Failed to upload PCD bundle.' && error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response;
        if (response?.data?.detail) {
          message = response.data.detail;
        }
      } else if (message === 'Failed to upload PCD bundle.' && error instanceof Error) {
        message = error.message;
      }
      setUploadError(message);
    } finally {
      setIsUploading(false);
      setUploadPhase('idle');
      setUploadPercent(0);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const beginSensorPropertyEdit = (sensor: Sensor, fieldKey: string) => {
    beginSensorPoseEdit(sensor.id, fieldKey);
  };

  const finishSensorPropertyEdit = () => {
    finishSensorPoseEdit();
  };

  const handleSensorPropertyChange = (
    prop: 'position' | 'rotation',
    axisIndex: number,
    value: string
  ) => {
    if (!selectedSensor) return;
    let newValue = parseFloat(value) || 0;
    if (prop === 'rotation') {
      newValue = toRadians(newValue);
    }
    const newPropValue = [...selectedSensor[prop]] as [number, number, number];
    newPropValue[axisIndex] = newValue;
    updateSensor(selectedSensor.id, { [prop]: newPropValue });
  };

  const handleAutoFloor = async (sensorId: string) => {
    setAlignmentActionMessage(null);
    setIsAutoFlooring(true);
    try {
      const result = await autoFloorSensor(sensorId);
      setAlignmentActionMessage(result.message);
    } finally {
      setIsAutoFlooring(false);
    }
  };

  const handleRunIcp = async (sensorId: string) => {
    setAlignmentActionMessage(null);
    setIsRunningIcp(true);
    try {
      const result = await runIcpSensor(sensorId);
      setAlignmentActionMessage(result.message);
    } finally {
      setIsRunningIcp(false);
    }
  };

  const handleModifyQortex = async (file: File) => {
    if (!currentProject) {
      setExportQortexMessage('Please select a project first.');
      return;
    }

    setExportQortexMessage(null);
    setIsExportingQortex(true);
    try {
      const blob = await modifyQortexXml(currentProject.id, file);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName = file.name.replace(/\.xml$/i, '');
      link.download = `${baseName}_aligned.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportQortexMessage(`Updated transforms in ${file.name}.`);
    } catch {
      setExportQortexMessage('Failed to update Q-TRACK settings. Check that sensor names match frame IDs.');
    } finally {
      setIsExportingQortex(false);
      if (qortexSettingsInputRef.current) {
        qortexSettingsInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (isQortexSyncOpen || isExportSettingsDialogOpen) {
        return;
      }
      if (
        target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      if (event.key.toLowerCase() === 't') {
        setTransformMode('translate');
        return;
      }
      if (event.key.toLowerCase() === 'r') {
        setTransformMode('rotate');
        return;
      }
      if (event.key === 'Escape') {
        setSelectedObjectId(null);
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        advanceAlignmentSensor(event.shiftKey ? 'prev' : 'next');
        return;
      }
      if ((event.key === 'f' || event.key === 'F') && selectedSensor?.pcdUrl) {
        event.preventDefault();
        void handleAutoFloor(selectedSensor.id);
        return;
      }
      if ((event.key === 'i' || event.key === 'I') && selectedSensor?.pcdUrl) {
        event.preventDefault();
        void handleRunIcp(selectedSensor.id);
        return;
      }
      if (event.key === ' ' && selectedSensor?.pcdUrl) {
        event.preventDefault();
        commitSensorAlignment(selectedSensor.id);
        return;
      }
      if (!selectedSensor?.pcdUrl) {
        return;
      }

      const fine = event.ctrlKey || event.metaKey;
      const coarse = event.shiftKey;
      const tStep = coarse ? 1.0 : fine ? 0.05 : TRANSLATE_STEP;
      const rStep = toRadians(coarse ? 30 : fine ? 0.5 : ROTATE_STEP_DEG);

      const position = [...selectedSensor.position] as [number, number, number];
      const rotation = [...selectedSensor.rotation] as [number, number, number];
      let changed = false;

      switch (event.key) {
        case 'ArrowUp':
          position[1] += tStep;
          changed = true;
          break;
        case 'ArrowDown':
          position[1] -= tStep;
          changed = true;
          break;
        case 'ArrowRight':
          position[0] += tStep;
          changed = true;
          break;
        case 'ArrowLeft':
          position[0] -= tStep;
          changed = true;
          break;
        case 'PageUp':
          if (!selectedSensor.floorLocked) {
            position[2] += tStep;
            changed = true;
          }
          break;
        case 'PageDown':
          if (!selectedSensor.floorLocked) {
            position[2] -= tStep;
            changed = true;
          }
          break;
        case 'l':
        case 'L':
          toggleFloorLock(selectedSensor, updateSensor, pushSensorPoseHistory);
          return;
        default:
          break;
      }

      if (event.code === 'Comma') {
        rotation[2] += rStep;
        changed = true;
      } else if (event.code === 'Period') {
        rotation[2] -= rStep;
        changed = true;
      }

      if (changed) {
        event.preventDefault();
        nudgeSensor(selectedSensor, updateSensor, pushSensorPoseHistory, { position, rotation });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedSensor,
    updateSensor,
    setTransformMode,
    setSelectedObjectId,
    commitSensorAlignment,
    advanceAlignmentSensor,
    autoFloorSensor,
    runIcpSensor,
    pushSensorPoseHistory,
    sensorPoseHistory.length,
    sensorPoseRedoHistory.length,
    isQortexSyncOpen,
    isExportSettingsDialogOpen,
  ]);

  const renderPoseControls = () => {
    if (!selectedSensor?.pcdUrl) {
      return null;
    }

    return (
      <div data-testid="alignment-pose-controls" style={{ marginTop: '10px', position: 'relative', minWidth: 0 }}>
        <MovementHelpButton />

        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', paddingRight: '32px' }}>
          <button
            type="button"
            style={{
              ...compactButtonStyle,
              flex: 1,
              background: transformMode === 'translate' ? '#4f46e5' : '#334155',
            }}
            data-testid="alignment-translate-mode"
            title="Translate mode (T)"
            onClick={() => setTransformMode('translate')}
          >
            Translate
          </button>
          <button
            type="button"
            style={{
              ...compactButtonStyle,
              flex: 1,
              background: transformMode === 'rotate' ? '#4f46e5' : '#334155',
            }}
            data-testid="alignment-rotate-mode"
            title="Rotate mode (R)"
            onClick={() => setTransformMode('rotate')}
          >
            Rotate
          </button>
        </div>

        <div style={{ fontSize: '12px', marginBottom: '4px', color: '#cbd5e1' }}>Position (m)</div>
        <div style={axisFieldGridStyle}>
          {(['X', 'Y', 'Z'] as const).map((axis, index) => (
            <label key={axis} style={axisLabelStyle}>
              {axis}
              <input
                type="number"
                step="0.01"
                style={axisInputStyle}
                data-testid={`alignment-position-${axis.toLowerCase()}`}
                title={
                  axis === 'Z' && selectedSensor.floorLocked
                    ? `${POSITION_FIELD_TITLES.Z}. Unlock floor to enable.`
                    : POSITION_FIELD_TITLES[axis]
                }
                value={formatInputNumber(selectedSensor.position[index])}
                onFocus={() => beginSensorPropertyEdit(selectedSensor, `position-${index}`)}
                onBlur={finishSensorPropertyEdit}
                onChange={(event) => handleSensorPropertyChange('position', index, event.target.value)}
              />
            </label>
          ))}
        </div>

        <div style={{ fontSize: '12px', marginBottom: '4px', color: '#cbd5e1' }}>Rotation (deg)</div>
        <div style={axisFieldGridStyle}>
          {(['Roll', 'Pitch', 'Yaw'] as const).map((label, index) => (
            <label key={label} style={axisLabelStyle}>
              {label}
              <input
                type="number"
                step="0.1"
                style={axisInputStyle}
                data-testid={`alignment-rotation-${label.toLowerCase()}`}
                title={ROTATION_FIELD_TITLES[label]}
                value={formatInputNumber(toDegrees(selectedSensor.rotation[index]), 1)}
                onFocus={() => beginSensorPropertyEdit(selectedSensor, `rotation-${index}`)}
                onBlur={finishSensorPropertyEdit}
                onChange={(event) => handleSensorPropertyChange('rotation', index, event.target.value)}
              />
            </label>
          ))}
        </div>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginBottom: '8px' }}
          title="Lock floor height and roll/pitch (L). ICP only adjusts XY and yaw when locked."
        >
          <input
            type="checkbox"
            checked={Boolean(selectedSensor.floorLocked)}
            data-testid="alignment-floor-lock"
            onChange={() => {
              toggleFloorLock(selectedSensor, updateSensor, pushSensorPoseHistory);
            }}
          />
          Floor locked
        </label>

        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-run-icp"
            title="Register scan to aligned neighbors (I). Needs overlap with committed sensors."
            disabled={isRunningIcp || isAutoFlooring}
            onClick={() => void handleRunIcp(selectedSensor.id)}
          >
            {isRunningIcp ? 'Running ICP...' : 'ICP'}
          </button>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-auto-floor"
            title="Level sensor on the detected floor plane (F). Sets floor locked."
            disabled={isAutoFlooring || isRunningIcp}
            onClick={() => void handleAutoFloor(selectedSensor.id)}
          >
            {isAutoFlooring ? 'Detecting...' : 'Auto-Floor'}
          </button>
        </div>
        {alignmentActionMessage && (
          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 6px' }} data-testid="alignment-status-message">
            {alignmentActionMessage}
          </p>
        )}

        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-undo"
            title="Revert last pose change (Ctrl+Z)"
            disabled={sensorPoseHistory.length === 0}
            onClick={() => undoSensorPose()}
          >
            Undo
          </button>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-redo"
            title="Restore undone pose change (Ctrl+Shift+Z)"
            disabled={sensorPoseRedoHistory.length === 0}
            onClick={() => redoSensorPose()}
          >
            Redo
          </button>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-reset-design"
            title="Restore design position and rotation from Q-TRACK import"
            disabled={!selectedSensor.designPosition || !selectedSensor.designRotation}
            onClick={() => resetSensorToDesign(selectedSensor.id)}
          >
            Reset
          </button>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1 }}
            data-testid="alignment-restore-aligned"
            title="Restore last committed aligned pose"
            disabled={!hasAlignedPoseSnapshot(selectedSensor) || !alignedPoseDiffersFromLive(selectedSensor)}
            onClick={() => restoreSensorToAligned(selectedSensor.id)}
          >
            Restore Aligned
          </button>
          <button
            type="button"
            style={{ ...compactButtonStyle, flex: 1, background: '#059669' }}
            data-testid="alignment-commit"
            title="Mark sensor aligned and keep pose (Space)"
            onClick={() => commitSensorAlignment(selectedSensor.id)}
          >
            Apply
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
    <div style={panelStyle} data-testid="alignment-panel">
      <h4 style={{ marginTop: 0 }}>Sensor Alignment</h4>

      <div
        style={{
          marginBottom: '12px',
          padding: '10px',
          borderRadius: '6px',
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid #334155',
          fontSize: '13px',
        }}
        data-testid="alignment-qortex-tools"
      >
        <div style={{ marginBottom: '8px', fontWeight: 600 }}>Q-TRACK</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: exportQortexMessage ? '8px' : 0 }}>
          <button
            type="button"
            style={{ ...secondaryButtonStyle, flex: 1 }}
            data-testid="alignment-qortex-sync"
            onClick={() => setIsQortexSyncOpen(true)}
          >
            Import
          </button>
          <button
            type="button"
            style={{ ...secondaryButtonStyle, flex: 1 }}
            data-testid="alignment-modify-qortex"
            disabled={isExportingQortex || sensors.length === 0}
            onClick={() => setIsExportSettingsDialogOpen(true)}
          >
            {isExportingQortex ? 'Exporting...' : 'Export'}
          </button>
          <input
            ref={qortexSettingsInputRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            style={{ display: 'none' }}
            data-testid="alignment-modify-qortex-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleModifyQortex(file);
              }
            }}
          />
        </div>
        {exportQortexMessage && (
          <p
            style={{ fontSize: '11px', color: '#94a3b8', margin: '8px 0 0' }}
            data-testid="alignment-modify-qortex-message"
          >
            {exportQortexMessage}
          </p>
        )}
      </div>

      {sensorsWithPcd.length > 0 && (
        <div
          style={{
            marginBottom: '12px',
            padding: '10px',
            borderRadius: '6px',
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid #334155',
            fontSize: '13px',
          }}
          data-testid="alignment-progress"
        >
          <button
            type="button"
            style={{ ...linkButtonStyle, width: '100%', textAlign: 'left', marginBottom: '8px' }}
            data-testid="alignment-progress-open"
            onClick={() => setIsPcdStatusOpen(true)}
          >
            Progress: <strong>{placedCount}</strong> / <strong>{totalAlignable}</strong> aligned
          </button>
          {originSensor && (
            <div style={{ color: '#94a3b8', marginBottom: '6px' }} data-testid="alignment-origin">
              Origin: <strong>{originSensor.name}</strong>
            </div>
          )}
          {recommendedNext ? (
            <div data-testid="alignment-recommended-next">
              Next:{' '}
              <button
                type="button"
                style={linkButtonStyle}
                data-testid="alignment-next-select"
                onClick={() => setSelectedObjectId(recommendedNext.sensor_id)}
              >
                {recommendedNext.sensor_name}
              </button>
              <span style={{ color: '#94a3b8', marginLeft: '8px' }}>
                {recommendedNext.distance_m.toFixed(1)}m
                {recommendedNext.has_los ? ' · LOS clear' : ' · blocked'}
              </span>
            </div>
          ) : (
            <div style={{ color: '#94a3b8' }} data-testid="alignment-no-recommendation">
              {placedCount >= totalAlignable ? 'All alignable sensors placed.' : 'Press Tab to select the next sensor.'}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        <input
          ref={inputRef}
          type="file"
          accept=".zip,.tar.gz,.tgz,application/zip,application/gzip,application/x-gzip,application/x-tar"
          data-testid="alignment-pcd-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              void handleUpload(file);
            }
          }}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          style={{
            ...buttonStyle,
            width: '100%',
            opacity: isUploading ? 0.7 : 1,
            cursor: isUploading ? 'wait' : 'pointer',
          }}
          disabled={isUploading || !currentProject}
          data-testid="alignment-upload-button"
          onClick={() => inputRef.current?.click()}
        >
          {uploadPhase === 'analyzing'
            ? 'Analyzing PCDs…'
            : uploadPhase === 'uploading'
              ? `Uploading… ${uploadPercent}%`
              : 'Upload PCD Archive'}
        </button>
        <UploadProgressBar phase={uploadPhase} percent={uploadPercent} />
      </div>

      {uploadError && (
        <p style={{ color: '#f87171', fontSize: '13px', marginTop: '10px' }} data-testid="alignment-upload-error">
          {uploadError}
        </p>
      )}

      {uploadResult && (
        <div
          style={{
            marginTop: '12px',
            padding: '10px',
            borderRadius: '6px',
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid #334155',
            fontSize: '13px',
          }}
          data-testid="alignment-upload-results"
        >
          <div style={{ marginBottom: '8px' }}>
            Matched <strong>{uploadResult.matched.filter((m) => m.status === 'matched').length}</strong> of{' '}
            <strong>{sensors.length}</strong> sensors
          </div>
          {uploadResult.missing_sensors.length > 0 && (
            <div data-testid="alignment-missing-sensors" style={{ color: '#fbbf24', marginBottom: '8px' }}>
              <strong>{uploadResult.missing_sensors.length}</strong> sensors not included in this upload.{' '}
              <button
                type="button"
                style={linkButtonStyle}
                data-testid="alignment-missing-open-status"
                onClick={() => setIsPcdStatusOpen(true)}
              >
                View PCD Status
              </button>
            </div>
          )}
          {uploadResult.unmatched_files.length > 0 && (
            <div data-testid="alignment-unmatched-files" style={{ color: '#fb923c', marginBottom: '8px' }}>
              Unmatched files: {uploadResult.unmatched_files.join(', ')}
            </div>
          )}
          {uploadResult.warnings.length > 0 && (
            <div data-testid="alignment-warnings" style={{ color: '#f87171' }}>
              {uploadResult.warnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: '12px',
          padding: '10px',
          borderRadius: '6px',
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid #334155',
          fontSize: '13px',
        }}
        data-testid="alignment-pcd-info"
      >
        <button
          type="button"
          style={{ ...linkButtonStyle, width: '100%', textAlign: 'left', marginBottom: '8px' }}
          data-testid="alignment-pcd-info-open"
          onClick={() => setIsPcdStatusOpen(true)}
        >
          <strong>{sensorsWithPcd.length}</strong> with PCD,{' '}
          <strong>{sensorsMissingPcd.length}</strong> without
        </button>
        {selectedSensor && (
          <div data-testid="alignment-selected-sensor">
            Selected: <strong>{selectedSensor.name}</strong>
            <span style={{ marginLeft: '8px' }}>
              {renderSensorStatusBadges(selectedSensor)}
            </span>
          </div>
        )}

        {renderPoseControls()}
      </div>

      {sensors.length > 0 && (
        <div style={{ marginTop: '12px', maxHeight: '180px', overflowY: 'auto' }} data-testid="alignment-sensor-list">
          {sensors.map((sensor) => (
            <div
              key={sensor.id}
              role="button"
              tabIndex={0}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
                fontSize: '12px',
                padding: '4px 0',
                borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
                cursor: sensor.pcdUrl ? 'pointer' : 'default',
                background:
                  sensor.id === selectedObjectId
                    ? 'rgba(79, 70, 229, 0.15)'
                    : sensor.id === recommendedNext?.sensor_id
                      ? 'rgba(56, 189, 248, 0.12)'
                      : 'transparent',
                borderLeft:
                  sensor.id === originSensorId
                    ? '3px solid #facc15'
                    : sensor.id === recommendedNext?.sensor_id
                      ? '3px solid #38bdf8'
                      : '3px solid transparent',
              }}
              onClick={() => {
                if (sensor.pcdUrl) {
                  setSelectedObjectId(sensor.id);
                }
              }}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && sensor.pcdUrl) {
                  setSelectedObjectId(sensor.id);
                }
              }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sensor.name}
              </span>
              {renderSensorStatusBadges(sensor)}
            </div>
          ))}
        </div>
      )}
    </div>
    <QortexSyncModal isOpen={isQortexSyncOpen} onClose={() => setIsQortexSyncOpen(false)} />
    <PcdStatusModal isOpen={isPcdStatusOpen} onClose={() => setIsPcdStatusOpen(false)} />
    {isExportSettingsDialogOpen && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}
        data-testid="alignment-export-settings-dialog"
      >
        <div
          style={{
            backgroundColor: '#1e293b',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #334155',
            minWidth: '360px',
            maxWidth: '440px',
          }}
        >
          <h3 style={{ margin: '0 0 12px 0', color: 'white' }}>Export settings</h3>
          <p style={{ margin: '0 0 20px 0', color: '#94a3b8', fontSize: '14px', lineHeight: 1.5 }}>
            Export replaces the sensor transforms in an existing Q-TRACK settings XML file with the
            aligned poses from this project. Select your settings file to update it.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setIsExportSettingsDialogOpen(false)}
              disabled={isExportingQortex}
              style={{
                padding: '8px 16px',
                backgroundColor: '#475569',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isExportingQortex ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="alignment-export-settings-confirm"
              disabled={isExportingQortex}
              onClick={() => {
                setIsExportSettingsDialogOpen(false);
                qortexSettingsInputRef.current?.click();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isExportingQortex ? 'not-allowed' : 'pointer',
              }}
            >
              Select settings file
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default AlignmentPanel;
